import { useEffect } from 'react';

import { IntegrationProps } from '#/lib/sanity/fetchers/fetchIntegrationProps';
import { SanityPageSection } from '#/lib/sanity/queries/sections';
import { useAnalytics } from '#/lib/segment';
import type { PageLayoutSharedProps } from '#/lib/types';
import { addViewedAlgoliaObjectID } from '#/lib/viewed-algolia-objects';
import { Button } from '#/components/Button';
import { Headline } from '#/components/Headline';
import { LinkCardGrid } from '#/components/LinkCardGrid';
import { PageSections } from '#/components/PageSections';

type Props = IntegrationProps & PageLayoutSharedProps;

export function IntegrationPage(props: Props) {
  const data = props.page;

  const analytics = useAnalytics();

  // Track the viewed integration in local storage - used for Algolia search re-ranking
  useEffect(() => {
    addViewedAlgoliaObjectID({
      objectID: data._id,
      algoliaObjectType: 'INTEGRATIONS',
    });
    analytics.track('Viewed Integration', {
      integrationName: data.title,
      integrationSlug: data.slug,
      integrationId: data._id,
    });
  }, [data._id, data.title, data.slug, analytics]);

  let pageSections: SanityPageSection[] = [];
  if (data.sections?.length > 0) {
    pageSections = [...data.sections];
  }
  if (data.detailSections?.length > 0) {
    pageSections = [...pageSections, ...data.detailSections];
  }

  const features = Object.entries(JSON.parse(props.page.featuresMetaData))
    .filter(([, value]) => Boolean(value))
    .map(([key]) => key);

  const cardsToShow = data.allCards
    .filter((card) =>
      card.integrationFeatureMatch?.some((feature) =>
        features.includes(feature),
      ),
    )
    .map((card) => card.card)
    // Malformed Sanity link cards can resolve link to null (e.g. object-shaped
    // link instead of array); skip them so SSR does not crash on .href.
    .filter((card) => Boolean(card?.link?.href));

  return (
    <div>
      <div className="p-main mt-[calc(var(--header-height)*-1)] rounded-b-20 bg-grayscale-01 pb-40 pt-[calc(var(--header-height)+3rem)] text-center m:rounded-b-35 m:pb-100">
        <div className="mx-auto w-full max-w-735">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={data.imageUrl}
            className="mx-auto size-34 m:size-44"
            alt={`${data.title} logo`}
          />
          <Headline
            as="h1"
            value={`Data decisioning with ${data.title}`}
            variant={2}
            className="mb-12 mt-15 m:mb-25 m:mt-20"
          />
          <p className="text-balance text-14 font-light leading-150 text-grayscale-06 m:text-16">
            {data.description}
          </p>
          {props.page.heroCta && (
            <Button
              href={props.page.heroCta.href}
              variant="secondary"
              className="mt-25 m:mt-30"
            >
              {props.page.heroCta.title}
            </Button>
          )}
        </div>
      </div>
      {cardsToShow.length > 0 && (
        <section className="my-60 m:my-120">
          <LinkCardGrid
            compressed
            _type="linkCardGrid"
            _key="supported-features"
            cards={cardsToShow}
            sectionIndex={0}
            headline="Supported features"
          />
        </section>
      )}
      {/* Overwrite index start as there is some negative margin logic in first module of pageSections */}
      <PageSections
        sections={pageSections}
        sectionIndex={1}
        initialData={props.initialData}
        isPreview={props.isPreview}
      />
    </div>
  );
}
